import { Controller } from 'stimulus';

// Job-side parameter fill-in (the edit_multiple modal): shows/hides whole
// parameter sections based on the answer to a parent dropdown parameter, the
// same way public forms toggle conditional sections. Hidden sections have their
// inputs disabled so they neither submit values nor block the browser on
// required fields. The dropdown "Custom?" toggle (checkbox-toggle) is left to
// manage which of its two inputs is active; we just re-trigger it after showing.
export default class extends Controller {
  static targets = ['section'];

  connect() {
    this.handler = () => this.evaluateAll();
    this.element.querySelectorAll('select, input, textarea').forEach((el) => {
      el.addEventListener('change', this.handler);
      el.addEventListener('input', this.handler);
    });
    this.evaluateAll();
  }

  evaluateAll() {
    // Re-entrancy guard: showSection() dispatches a synthetic `change` on nested
    // checkbox-toggle checkboxes to re-sync their inputs, and this controller also
    // listens to `change` on every input. Without this guard that dispatch would
    // re-enter evaluateAll() and recurse until the stack overflows. The separate
    // checkbox-toggle controller still receives the `change` and does its work.
    if (this.evaluating) return;
    this.evaluating = true;
    try {
      this.sectionTargets.forEach((sectionEl) => {
        const parentParameterId = sectionEl.dataset.parentParameterId;
        if (!parentParameterId) return; // non-conditional section, always shown

        const conditionValues = JSON.parse(sectionEl.dataset.conditionValues || '[]');
        const parentValues = this.findParentValues(parentParameterId);
        const conditionMet = conditionValues.some((cv) => parentValues.includes(cv));

        if (conditionMet) {
          this.showSection(sectionEl);
        } else {
          this.hideSection(sectionEl);
        }
      });
    } finally {
      this.evaluating = false;
    }
  }

  findParentValues(parentParameterId) {
    const wrapper = this.element.querySelector(`[data-parameter-id="${parentParameterId}"]`);
    if (!wrapper) return [];

    const values = [];
    wrapper.querySelectorAll('select').forEach((select) => {
      if (select.value && !select.disabled) values.push(select.value);
    });
    wrapper.querySelectorAll('input[type="text"]').forEach((input) => {
      if (input.value && !input.disabled) values.push(input.value);
    });
    return values;
  }

  showSection(sectionEl) {
    const wasHidden = sectionEl.classList.contains('hidden');
    sectionEl.classList.remove('hidden');
    sectionEl.querySelectorAll('input, select, textarea').forEach((input) => {
      if (input.type === 'hidden') return;
      input.removeAttribute('disabled');
    });
    // Let a nested checkbox-toggle re-disable the inactive dropdown input so
    // only one value input submits.
    sectionEl.querySelectorAll('[data-target="checkbox-toggle.checkbox"]').forEach((checkbox) => {
      checkbox.dispatchEvent(new Event('change'));
    });
    // Fade the section in only when it actually transitions from hidden to
    // visible (not on initial load or on every keystroke).
    if (wasHidden) {
      sectionEl.classList.remove('tp-cond-enter');
      void sectionEl.offsetWidth; // force reflow so the animation replays
      sectionEl.classList.add('tp-cond-enter');
    }
  }

  hideSection(sectionEl) {
    sectionEl.classList.add('hidden');
    sectionEl.querySelectorAll('input, select, textarea').forEach((input) => {
      if (input.type === 'hidden') return;
      input.setAttribute('disabled', 'disabled');
    });
  }
}
